"use client";

import React from "react";
import { useTranslations } from "next-intl";
import { FaCalculator, FaChartLine, FaUsers, FaBook, FaNewspaper } from "react-icons/fa";
import Prism from "prismjs";
import "prismjs/themes/prism-tomorrow.css";
import "prismjs/components/prism-typescript";
import "prismjs/components/prism-javascript";

export default function MetricsDocs() {
	const t = useTranslations('docs.metrics');

	React.useEffect(() => {
		Prism.highlightAll();
	}, []);

	const qScoreArticleExample = `
// Q Score calculation for Journal Articles
async getQScore(authorShare: number, impactFactor: number) {
  if (
    this.otype === "JournalArticle" &&
    this.journal?.reviewType === "REVIEWED" &&
    this.isValidArticleType()
  ) {
    // With Impact Factor
    if (impactFactor > 0) {
      this.qscore = Math.max(0.6, impactFactor) * authorShare;
    } 
    // Without Impact Factor
    else {
      this.qscore = (this.foreignEdition ? 0.4 : 0.3) * authorShare;
    }
    return { qscore: this.qscore, source: "articles" };
  }
}`;

	const qScoreBookExample = `
// Q Score calculation for Books
if (this.isBook() || this.isBookChapter()) {
  const pageLength = this.getPageLength();
  
  if (pageLength >= 10) {
    let baseScore;
    
    // Books with 100+ pages
    if (pageLength >= 100) {
      baseScore = this.foreignLanguage ? 2 : 1;
    } 
    // Books with 10-99 pages
    else {
      baseScore = (this.foreignLanguage ? 0.2 : 0.1) * 
                  Math.floor(pageLength / 10);
    }
    
    this.qscore = baseScore * authorShare;
    return { qscore: this.qscore, source: "books" };
  }
}`;

	const iScoreExample = `
// I Score calculation
getIScore() {
  let iscoreWos = 0;
  
  if (this.published && this.citations) {
    this.iscore = this.citations.reduce((score, citation) => {
      if (citation.externalCitation && citation.related?.type?.label) {
        const citingType = citation.related.type.label;
        
        // Only count scientific publication types
        if ([
          "Könyvrészlet",
          "Könyv",
          "Folyóiratcikk",
          "Egyéb konferenciaközlemény"
        ].includes(citingType)) {
          
          // Check for Web of Science citation
          if (citation.related.identifiers?.some((i) =>
            ["WoS", "Wos-CSCD (Chinese)"].includes(i.source?.label || "")
          )) {
            iscoreWos++;
            return score + 1;
          }
          
          return score + 1;
        }
      }
      return score;
    }, 0);
    
    // Count scientific citations (exclude Thesis and Other)
    this.scientificCitationCount = this.citations.reduce(
      (acc, citation) =>
        citation.related?.otype !== "Thesis" &&
        citation.related?.otype !== "PublicationOther"
          ? acc + 1
          : acc,
      0
    );
    
    return {
      iscore: this.iscore,
      iscoreWos,
      scientificCitations: this.scientificCitationCount
    };
  }
  
  return { iscore: 0, iscoreWos: 0, scientificCitations: 0 };
}`;

	const authorShareExample = `
// Author Share calculation
getAuthorShare(mtid: string): number {
  const authorships = this._data.authorships || [];
  
  // Find explicit share for this author
  for (const authorship of authorships) {
    if (
      authorship.mtid === Number(mtid) &&
      authorship.share &&
      authorship.share > 0
    ) {
      return authorship.share;
    }
  }
  
  // Default: equal share among all authors
  return authorships.length > 0 ? 1 / authorships.length : 1;
}`;

	const hIndexExample = `
// H-Index calculation
const calculateHIndex = (citations: number[]): number => {
  if (!citations.length) return 0;
  
  // Sort citations in descending order
  citations.sort((a, b) => b - a);
  
  // Find the largest h where h papers have at least h citations
  return citations.findIndex((c, i) => c <= i) || citations.length;
};

// Example: [10, 8, 5, 4, 3] -> h-index = 4
// (4 papers have at least 4 citations each)`;

	const impactFactorExample = `
// Impact Factor lookup
const getImpactFactor = async (pub: Publication): Promise<number> => {
  if (!pub.journal) return 0;
  
  if (!IFData) await loadImpactFactorData();
  if (!IFData || !maxIFYear) return 0;
  
  const targetYear = Math.min(pub.publishedYear, maxIFYear);
  
  // Match by ISSN or journal name
  const exactMatch = IFData.find(
    (ifct) =>
      ifct.year === targetYear &&
      (ifct.eissn === pub.journal?.eIssn ||
       ifct.pissn === pub.journal?.pIssn ||
       ifct.eissn === pub.journal?.pIssn ||
       ifct.pissn === pub.journal?.eIssn ||
       (pub.journal?.label &&
        ifct.journal.toUpperCase() === pub.journal.label.toUpperCase()))
  );
  
  return exactMatch?.if ?? 0;
};`;

	return (
		<div className="prose prose-slate max-w-none prose-sm">
				<h1 className="flex items-center gap-3 text-2xl font-bold mb-4">
					<FaCalculator className="text-bme-main" />
					{t('title')}
				</h1>

				<p className="text-base text-gray-600 mb-6">
					{t('subtitle')}
				</p>

				<div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg mb-6">
					<p className="text-yellow-800 m-0 font-bold">
						<strong>{t('norwayExclusionNote')}</strong>
					</p>
				</div>

				{/* Q Score Section */}
				<section id="q-score" className="scroll-mt-20">
					<h2 className="text-xl font-bold mt-8 mb-4 flex items-center gap-3">
						<FaChartLine className="text-bme-main" />
						{t('qScore.title')}
					</h2>

					<p className="text-lg mb-4">
						{t('qScore.description')}
					</p>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('qScore.journalArticles.title')}</h3>
					<p className="mb-4">
						{t('qScore.journalArticles.description')}
					</p>
					<ul className="list-disc pl-6 mb-4 space-y-2">
						<li><strong>{t('qScore.journalArticles.withIF')}</strong> {t('qScore.journalArticles.withIFFormula')}</li>
						<li><strong>{t('qScore.journalArticles.withoutIF')}</strong> 
							<ul className="list-disc pl-6 mt-2">
								<li>{t('qScore.journalArticles.foreignEdition')}</li>
								<li>{t('qScore.journalArticles.hungarianEdition')}</li>
							</ul>
						</li>
					</ul>

					<div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-lg mb-6">
						<p className="text-blue-800 m-0 font-bold">
							<strong>{t('qScore.journalArticles.requirements')}</strong> {t('qScore.journalArticles.requirementsText')}
						</p>
					</div>

					<pre className="mt-4!">
						<code className="language-typescript">{qScoreArticleExample}</code>
					</pre>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('qScore.books.title')}</h3>
					<p className="mb-4">
						{t('qScore.books.description')}
					</p>
					<ul className="list-disc pl-6 mb-4 space-y-2">
						<li><strong>{t('qScore.books.hundredPlus')}</strong>
							<ul className="list-disc pl-6 mt-2">
								<li>{t('qScore.books.hundredPlusForeign')}</li>
								<li>{t('qScore.books.hundredPlusHungarian')}</li>
							</ul>
						</li>
						<li><strong>{t('qScore.books.tenToNinetyNine')}</strong>
							<ul className="list-disc pl-6 mt-2">
								<li>{t('qScore.books.tenToNinetyNineForeign')}</li>
								<li>{t('qScore.books.tenToNinetyNineHungarian')}</li>
							</ul>
						</li>
						<li><strong>{t('qScore.books.lessThanTen')}</strong></li>
					</ul>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('qScore.conference.title')}</h3>
					<p className="mb-4">
						{t('qScore.conference.description')}
					</p>
					<ul className="list-disc pl-6 mb-4 space-y-2">
						<li>{t('qScore.conference.foreign')}</li>
						<li>{t('qScore.conference.hungarian')}</li>
					</ul>

					<pre className="mt-4!">
						<code className="language-typescript">{qScoreBookExample}</code>
					</pre>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('qScore.aggregation.title')}</h3>
					<p className="mb-4">
						{t('qScore.aggregation.description')}
					</p>
					<ul className="list-disc pl-6 mb-4 space-y-2">
						<li><strong>{t('qScore.aggregation.fromArticles')}</strong> {t('qScore.aggregation.fromArticlesDesc')}</li>
						<li><strong>{t('qScore.aggregation.fromBooks')}</strong> {t('qScore.aggregation.fromBooksDesc')}</li>
						<li><strong>{t('qScore.aggregation.total')}</strong> {t('qScore.aggregation.totalDesc')}</li>
					</ul>
				</section>

				{/* I Score Section */}
				<section id="i-score" className="scroll-mt-20">
					<h2 className="text-xl font-bold mt-8 mb-4 flex items-center gap-3">
						<FaChartLine className="text-bme-main" />
						{t('iScore.title')}
					</h2>

					<p className="text-lg mb-4">
						{t('iScore.description')}
					</p>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('iScore.calculationRules.title')}</h3>
					<ul className="list-disc pl-6 mb-4 space-y-2">
						<li>{t('iScore.calculationRules.externalOnly')}</li>
						<li>{t('iScore.calculationRules.scientificTypes')}
							<ul className="list-disc pl-6 mt-2">
								<li>{t('iScore.calculationRules.bookChapter')}</li>
								<li>{t('iScore.calculationRules.book')}</li>
								<li>{t('iScore.calculationRules.journalArticle')}</li>
								<li>{t('iScore.calculationRules.conferencePaper')}</li>
							</ul>
						</li>
						<li>{t('iScore.calculationRules.excludes')}
							<ul className="list-disc pl-6 mt-2">
								<li>{t('iScore.calculationRules.thesis')}</li>
								<li>{t('iScore.calculationRules.other')}</li>
							</ul>
						</li>
					</ul>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('iScore.wos.title')}</h3>
					<p className="mb-4">
						{t('iScore.wos.description')}
					</p>

					<pre className="mt-4!">
						<code className="language-typescript">{iScoreExample}</code>
					</pre>
				</section>

				{/* Impact Factor Section */}
				<section id="impact-factor" className="scroll-mt-20">
					<h2 className="text-xl font-bold mt-8 mb-4 flex items-center gap-3">
						<FaNewspaper className="text-bme-main" />
						{t('impactFactor.title')}
					</h2>

					<p className="text-lg mb-4">
						{t('impactFactor.description')}
					</p>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('impactFactor.matchingProcess.title')}</h3>
					<ol className="list-decimal pl-6 mb-4 space-y-2">
						<li>{t('impactFactor.matchingProcess.step1')}</li>
						<li>{t('impactFactor.matchingProcess.step2')}</li>
						<li>{t('impactFactor.matchingProcess.step3')}
							<ul className="list-disc pl-6 mt-2">
								<li>{t('impactFactor.matchingProcess.eissn')}</li>
								<li>{t('impactFactor.matchingProcess.pissn')}</li>
								<li>{t('impactFactor.matchingProcess.journalName')}</li>
							</ul>
						</li>
					</ol>

					<div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg mb-6">
						<p className="text-yellow-800 m-0 font-bold">
							<strong>{t('impactFactor.note')}</strong> {t('impactFactor.noteText')}
						</p>
					</div>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('impactFactor.metrics.title')}</h3>
					<ul className="list-disc pl-6 mb-4 space-y-2">
						<li><strong>{t('impactFactor.metrics.total')}</strong> {t('impactFactor.metrics.totalDesc')}</li>
						<li><strong>{t('impactFactor.metrics.relative')}</strong> {t('impactFactor.metrics.relativeDesc')}</li>
						<li><strong>{t('impactFactor.metrics.publicationsWithIF')}</strong> {t('impactFactor.metrics.publicationsWithIFDesc')}</li>
						<li><strong>{t('impactFactor.metrics.singleAuthor')}</strong> {t('impactFactor.metrics.singleAuthorDesc')}</li>
					</ul>

					<pre className="mt-4!">
						<code className="language-typescript">{impactFactorExample}</code>
					</pre>
				</section>

				{/* H-Index Section */}
				<section id="h-index" className="scroll-mt-20">
					<h2 className="text-xl font-bold mt-8 mb-4 flex items-center gap-3">
						<FaChartLine className="text-bme-main" />
						{t('hIndex.title')}
					</h2>

					<p className="text-lg mb-4">
						{t('hIndex.description')}
					</p>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('hIndex.definition.title')}</h3>
					<p className="mb-4">
						{t('hIndex.definition.text')}
					</p>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('hIndex.algorithm.title')}</h3>
					<ol className="list-decimal pl-6 mb-4 space-y-2">
						<li>{t('hIndex.algorithm.step1')}</li>
						<li>{t('hIndex.algorithm.step2')}</li>
						<li>{t('hIndex.algorithm.step3')}</li>
					</ol>

					<div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mb-6">
						<h4 className="font-semibold mb-2">{t('hIndex.example.title')}</h4>
						<p className="mb-2">{t('hIndex.example.citations')}</p>
						<ul className="list-disc pl-6">
							<li>Position 1: 10 citations ≥ 1 → ✓</li>
							<li>Position 2: 8 citations ≥ 2 → ✓</li>
							<li>Position 3: 5 citations ≥ 3 → ✓</li>
							<li>Position 4: 4 citations ≥ 4 → ✓</li>
							<li>Position 5: 3 citations ≥ 5 → ✗</li>
						</ul>
						<p className="mt-2"><strong>{t('hIndex.example.result')}</strong></p>
					</div>

					<pre className="mt-4!">
						<code className="language-typescript">{hIndexExample}</code>
					</pre>
				</section>

				{/* Author Share Section */}
				<section id="author-share" className="scroll-mt-20">
					<h2 className="text-xl font-bold mt-8 mb-4 flex items-center gap-3">
						<FaUsers className="text-bme-main" />
						{t('authorShare.title')}
					</h2>

					<p className="text-lg mb-4">
						{t('authorShare.description')}
					</p>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('authorShare.calculationRules.title')}</h3>
					<ol className="list-decimal pl-6 mb-4 space-y-2">
						<li>{t('authorShare.calculationRules.step1')}</li>
						<li>{t('authorShare.calculationRules.step2')}</li>
						<li>{t('authorShare.calculationRules.step3')}</li>
					</ol>

					<div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-lg mb-6">
						<p className="text-blue-800 m-0 font-bold">
							<strong>{t('authorShare.example.text')}</strong> {t('authorShare.example.description')}
						</p>
					</div>

					<pre className="mt-4!">
						<code className="language-typescript">{authorShareExample}</code>
					</pre>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('authorShare.usage.title')}</h3>
					<ul className="list-disc pl-6 mb-4 space-y-2">
						<li><strong>{t('authorShare.usage.qScore')}</strong></li>
						<li><strong>{t('authorShare.usage.relativeIF')}</strong></li>
						<li>{t('authorShare.usage.other')}</li>
					</ul>
				</section>

				{/* Publication Ratings Section */}
				<section id="ratings" className="scroll-mt-20">
					<h2 className="text-xl font-bold mt-8 mb-4 flex items-center gap-3">
						<FaBook className="text-bme-main" />
						{t('ratings.title')}
					</h2>

					<p className="text-lg mb-4">
						{t('ratings.description')}
					</p>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('ratings.categories.title')}</h3>
					<ul className="list-disc pl-6 mb-4 space-y-2">
						<li><strong>{t('ratings.categories.d1')}</strong></li>
						<li><strong>{t('ratings.categories.q1')}</strong></li>
						<li><strong>{t('ratings.categories.q2')}</strong></li>
						<li><strong>{t('ratings.categories.q3')}</strong></li>
						<li><strong>{t('ratings.categories.q4')}</strong></li>
						<li><strong>{t('ratings.categories.na')}</strong></li>
					</ul>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('ratings.source.title')}</h3>
					<p className="mb-4">
						{t('ratings.source.description')}
					</p>

					<div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mb-6">
						<h4 className="font-semibold mb-2">{t('ratings.yearlyAggregation.title')}</h4>
						<p>
							{t('ratings.yearlyAggregation.description')}
						</p>
					</div>
				</section>

				{/* Data Sources Section */}
				<section id="data-sources" className="scroll-mt-20">
					<h2 className="text-xl font-bold mt-8 mb-4">{t('dataSources.title')}</h2>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('dataSources.ifList.title')}</h3>
					<p className="mb-4">
						{t('dataSources.ifList.description')}
					</p>
					<ul className="list-disc pl-6 mb-4 space-y-2">
						<li>{t('dataSources.ifList.journalName')}</li>
						<li>{t('dataSources.ifList.pissn')}</li>
						<li>{t('dataSources.ifList.eissn')}</li>
						<li>{t('dataSources.ifList.ifValue')}</li>
						<li>{t('dataSources.ifList.quartile')}</li>
						<li>{t('dataSources.ifList.categoryMedian')}</li>
						<li>{t('dataSources.ifList.year')}</li>
					</ul>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('dataSources.norwayList.title')}</h3>
					<p className="mb-4">
						{t('dataSources.norwayList.description')}
					</p>
					<ul className="list-disc pl-6 mb-4 space-y-2">
						<li>{t('dataSources.norwayList.pissn')}</li>
						<li>{t('dataSources.norwayList.levels')}</li>
						<ul className="list-disc pl-6 mt-2 space-y-1">
							<li>{t('dataSources.norwayList.level0')}</li>
							<li>{t('dataSources.norwayList.level1')}</li>
							<li>{t('dataSources.norwayList.level2')}</li>
						</ul>
					</ul>
				</section>
			</div>
	);
}
